import { useState } from 'react';
import { useSelector } from 'react-redux';
import { MiniLoader, PageTitle, Button } from '@pwc/pwc-deals-ui';
import { getProjectId } from 'features/project/projectSlice';
import { runPerformanceTest } from './PerformanceTestService';
import { PerformanceTestResults } from './PerformanceTestTypes';

import './PerformanceTestPage.scss';

const statuses = {
  idle: 0,
  inProgress: 1,
  completed: 2,
  error: 3,
};

const PerformanceTestPage = () => {
  const [fileName, setFileName] = useState('');
  const [downloadStatus, setStatus] = useState(statuses.idle);
  const [downloadResults, setResults] = useState<PerformanceTestResults>();
  const projectId = useSelector(getProjectId);

  const onFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFileName(e.target.value);

  const download = async (file: string) => {
    setStatus(statuses.inProgress);
    try {
      const { results } = await runPerformanceTest(projectId, file);
      setStatus(statuses.completed);
      setResults(results);
    } catch (ex) {
      setStatus(statuses.error);
    }
  };

  const onDownloadAllClick = () => download('');
  const onDownloadOneClick = () => download(fileName);

  return (
    <div className="perf-test-page">
      <PageTitle title="Sharepoint Performance Test" size="h2" />
      <p>
        This page is for testing how long it takes to download files from
        Sharepoint.
      </p>
      <p>You can download all the files in the Sharepoint folder </p>
      <Button
        disabled={downloadStatus === statuses.inProgress}
        data-testid="download-all-button"
        type="button"
        onClick={onDownloadAllClick}
        className="button-block button-small"
        icon={undefined}
        iconClasses={undefined}
      >
        Download All
      </Button>
      <p>
        Or, enter the name of an individual file in the folder to download just
        that one.
      </p>
      <div className="download-one-container">
        <input type="text" onChange={onFileNameChange} value={fileName} />
        <Button
          disabled={
            fileName.length === 0 || downloadStatus === statuses.inProgress
          }
          data-testid="download-one-button"
          type="button"
          onClick={onDownloadOneClick}
          className="button-block button-small"
          icon={undefined}
          iconClasses={undefined}
        >
          Download File
        </Button>
      </div>
      {downloadStatus === statuses.error && (
        <div>
          <PageTitle title="Results" size="h3" />
          <p>
            An error occurred. Please make sure Sharepoint performance test
            folder is set up correctly and try again.
          </p>
        </div>
      )}
      {downloadStatus === statuses.completed && (
        <div className="results">
          <PageTitle title="Results" size="h3" />
          {downloadResults && downloadResults.length > 0 && (
            <table>
              <thead>
                <th>File Name</th>
                <th>Status</th>
                <th>Bytes</th>
                <th>Seconds</th>
              </thead>
              <tbody>
                {downloadResults.map((result) => (
                  <tr key={result.fileName}>
                    <td>{result.fileName}</td>
                    <td>{result.success ? 'Success' : 'Failed'}</td>
                    <td className="right">{result.byteCount}</td>
                    <td>{result.durationInSeconds}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!downloadResults?.length && <p>No files were found.</p>}
        </div>
      )}
      {downloadStatus === statuses.inProgress && (
        <div className="in-progress-container">
          <MiniLoader />
          <span>Downloading...</span>
        </div>
      )}
    </div>
  );
};

export default PerformanceTestPage;
